import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import "./BookingServices.css";
import { 
  faPlus,
  faPencilAlt,
  faTrashAlt,
  faEye,
  faSave,
  faBan,
  faTimesCircle,
  faMoneyBillWave,
  faWallet,
  faCoins,
  faPercent,
  faBrush,
  faUsers,
  faMusic,
  faChartBar,
  faUtensils,
  faShieldAlt,
  faTruck,
  faEllipsisH,
  faDownload,
  faHistory
} from '@fortawesome/free-solid-svg-icons';
import "./BudgetPlanner.css";
import axios from "axios";

// API Base URL
const API_BASE_URL = 'https://royaleheritagefiesta.com/royaleheritagefiesta/api';

function BudgetPlanner({ event, onClose, onSave }) {
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [budgetForm, setBudgetForm] = useState({
    eventId: event?.eventId || "",
    eventName: event?.eventName || "",
    eventDate: event?.eventDate || "",
    totalBudget: "",
    decorationBudget: "",
    staffBudget: "",
    soundBudget: "",
    marketingBudget: "",
    cateringBudget: "",
    securityBudget: "",
    transportBudget: "",
    miscellaneousBudget: "",
    notes: "",
    status: "Draft"
  });
  
  const [expenseForm, setExpenseForm] = useState({
    category: "",
    amount: "",
    description: ""
  });

  const [expenses, setExpenses] = useState([]);

  // Fetch budget when event changes
  useEffect(() => {
    if (event?.eventId) {
      fetchBudget();
    }
  }, [event]);

  const fetchBudget = async () => {
    setLoading(true);
    try {
      // UPDATED: Fetch budget from PHP API
      const response = await axios.get(`${API_BASE_URL}/budget.php?eventId=${event.eventId}`);
      console.log("Budget response:", response.data);
      
      if (response.data && response.data.budgetId) {
        setBudget(response.data);
        // Fetch expenses for this budget
        fetchExpenses(response.data.budgetId);
      } else {
        setBudget(null);
        setExpenses([]);
      }
    } catch (error) {
      console.error("Error fetching budget:", error);
      setBudget(null);
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchExpenses = async (budgetId) => {
    try {
      // UPDATED: Fetch expenses from PHP API
      const response = await axios.get(`${API_BASE_URL}/budget.php?budgetId=${budgetId}`);
      console.log("Expenses response:", response.data);
      
      if (response.data && response.data.expenses) {
        setExpenses(response.data.expenses);
      } else if (Array.isArray(response.data)) {
        setExpenses(response.data);
      } else {
        setExpenses([]);
      }
    } catch (error) {
      console.error("Error fetching expenses:", error);
      setExpenses([]);
    }
  };

  const createBudget = async () => {
    try {
      setLoading(true);
      
      // Calculate totals
      const decoration = parseFloat(budgetForm.decorationBudget) || 0;
      const staff = parseFloat(budgetForm.staffBudget) || 0;
      const sound = parseFloat(budgetForm.soundBudget) || 0;
      const marketing = parseFloat(budgetForm.marketingBudget) || 0;
      const catering = parseFloat(budgetForm.cateringBudget) || 0;
      const security = parseFloat(budgetForm.securityBudget) || 0;
      const transport = parseFloat(budgetForm.transportBudget) || 0;
      const misc = parseFloat(budgetForm.miscellaneousBudget) || 0;
      
      const totalAllocated = decoration + staff + sound + marketing + catering + security + transport + misc;
      const totalBudget = parseFloat(budgetForm.totalBudget) || 0;
      
      const budgetData = {
        eventId: event.eventId,
        eventName: event.eventName,
        eventDate: event.eventDate,
        totalBudget: totalBudget,
        decorationBudget: decoration,
        staffBudget: staff,
        soundBudget: sound,
        marketingBudget: marketing,
        cateringBudget: catering,
        securityBudget: security,
        transportBudget: transport,
        miscellaneousBudget: misc,
        totalExpenses: 0,
        remainingBudget: totalBudget - totalAllocated,
        notes: budgetForm.notes,
        status: budgetForm.status,
        createdBy: "Admin"
      };
      
      // UPDATED: Create budget via PHP API
      const response = await axios.post(`${API_BASE_URL}/budget.php`, budgetData);
      
      if (response.data.success) {
        alert("Budget created successfully!");
        setShowAddModal(false);
        resetBudgetForm();
        fetchBudget();
        if (onSave) onSave();
      } else {
        alert("Error creating budget: " + (response.data.error || "Unknown error"));
      }
    } catch (error) {
      console.error("Error:", error);
      alert("Error creating budget: " + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const updateBudget = async () => {
    if (!budget) return;
    
    try {
      setLoading(true);
      
      // Calculate totals
      const decoration = parseFloat(budgetForm.decorationBudget) || 0;
      const staff = parseFloat(budgetForm.staffBudget) || 0;
      const sound = parseFloat(budgetForm.soundBudget) || 0;
      const marketing = parseFloat(budgetForm.marketingBudget) || 0;
      const catering = parseFloat(budgetForm.cateringBudget) || 0;
      const security = parseFloat(budgetForm.securityBudget) || 0;
      const transport = parseFloat(budgetForm.transportBudget) || 0;
      const misc = parseFloat(budgetForm.miscellaneousBudget) || 0;
      
      const totalAllocated = decoration + staff + sound + marketing + catering + security + transport + misc;
      const totalBudget = parseFloat(budgetForm.totalBudget) || 0;
      
      const budgetData = {
        budgetId: budget.budgetId,
        eventId: budget.eventId,
        eventName: budget.eventName,
        eventDate: budget.eventDate,
        totalBudget: totalBudget,
        decorationBudget: decoration,
        staffBudget: staff,
        soundBudget: sound,
        marketingBudget: marketing,
        cateringBudget: catering,
        securityBudget: security,
        transportBudget: transport,
        miscellaneousBudget: misc,
        totalExpenses: budget.totalExpenses || 0,
        remainingBudget: totalBudget - totalAllocated,
        notes: budgetForm.notes,
        status: budgetForm.status,
        updatedBy: "Admin"
      };
      
      // UPDATED: Update budget via PHP API
      const response = await axios.put(`${API_BASE_URL}/budget.php`, budgetData);
      
      if (response.data.success) {
        alert("Budget updated successfully!");
        setShowEditModal(false);
        fetchBudget();
        if (onSave) onSave();
      } else {
        alert("Error updating budget: " + (response.data.error || "Unknown error"));
      }
    } catch (error) {
      console.error("Error:", error);
      alert("Error updating budget: " + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const deleteBudget = async () => {
    if (!budget) return;
    
    if (!window.confirm("Are you sure you want to delete this budget? This will also delete all associated expenses.")) {
      return;
    }
    
    try {
      setLoading(true);
      // UPDATED: Delete budget via PHP API
      const response = await axios.delete(`${API_BASE_URL}/budget.php?budgetId=${budget.budgetId}`);
      
      if (response.data.success) {
        alert("Budget deleted successfully!");
        setBudget(null);
        setExpenses([]);
        if (onSave) onSave();
      } else {
        alert("Error deleting budget: " + (response.data.error || "Unknown error"));
      }
    } catch (error) {
      console.error("Error:", error);
      alert("Error deleting budget: " + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const addExpense = async () => {
    if (!budget) return;
    
    try {
      setLoading(true);
      
      const expenseData = {
        budgetId: budget.budgetId,
        eventId: event.eventId,
        category: expenseForm.category,
        amount: parseFloat(expenseForm.amount),
        description: expenseForm.description,
        expenseDate: event.eventDate
      };
      
      // UPDATED: Add expense via PHP API
      const response = await axios.post(`${API_BASE_URL}/expenses.php`, expenseData);
      
      if (response.data.success) {
        alert("Expense added successfully!");
        setShowExpenseModal(false);
        resetExpenseForm();
        fetchBudget(); // Refresh to get updated totals
        fetchExpenses(budget.budgetId);
      } else {
        alert("Error adding expense: " + (response.data.error || "Unknown error"));
      }
    } catch (error) {
      console.error("Error:", error);
      alert("Error adding expense: " + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const deleteExpense = async (expenseId) => {
    if (!window.confirm("Are you sure you want to delete this expense?")) return;
    
    try {
      // UPDATED: Delete expense via PHP API
      const response = await axios.delete(`${API_BASE_URL}/expenses.php?expenseId=${expenseId}`);
      
      if (response.data.success) {
        alert("Expense deleted successfully!");
        fetchBudget(); // Refresh to get updated totals
        fetchExpenses(budget.budgetId);
      } else {
        alert("Error deleting expense: " + (response.data.error || "Unknown error"));
      }
    } catch (error) {
      console.error("Error deleting expense:", error);
      alert("Error deleting expense: " + (error.response?.data?.error || error.message));
    }
  };

  const exportBudgetReport = () => {
    if (!budget) return;
    
    const headers = ['Category', 'Allocated', 'Spent', 'Remaining', 'Utilization %'];
    
    const categories = [
      ['Decoration', budget.decorationBudget || 0, 
       expenses.filter(e => e.category === 'Decoration').reduce((sum, e) => sum + e.amount, 0)],
      ['Staff', budget.staffBudget || 0,
       expenses.filter(e => e.category === 'Staff').reduce((sum, e) => sum + e.amount, 0)],
      ['Sound', budget.soundBudget || 0,
       expenses.filter(e => e.category === 'Sound').reduce((sum, e) => sum + e.amount, 0)],
      ['Marketing', budget.marketingBudget || 0,
       expenses.filter(e => e.category === 'Marketing').reduce((sum, e) => sum + e.amount, 0)],
      ['Catering', budget.cateringBudget || 0,
       expenses.filter(e => e.category === 'Catering').reduce((sum, e) => sum + e.amount, 0)],
      ['Security', budget.securityBudget || 0,
       expenses.filter(e => e.category === 'Security').reduce((sum, e) => sum + e.amount, 0)],
      ['Transport', budget.transportBudget || 0,
       expenses.filter(e => e.category === 'Transport').reduce((sum, e) => sum + e.amount, 0)],
      ['Miscellaneous', budget.miscellaneousBudget || 0,
       expenses.filter(e => e.category === 'Miscellaneous').reduce((sum, e) => sum + e.amount, 0)]
    ];
    
    const rows = categories.map(([cat, allocated, spent]) => {
      const remaining = allocated - spent;
      const utilization = allocated > 0 ? ((spent / allocated) * 100).toFixed(1) : 0;
      return [cat, allocated, spent, remaining, utilization + '%'];
    });
    
    // Add summary row
    rows.push(['TOTAL', budget.totalBudget, budget.totalExpenses, 
               budget.totalBudget - budget.totalExpenses, 
               ((budget.totalExpenses / budget.totalBudget) * 100).toFixed(1) + '%']);
    
    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `budget-${event.eventName}-${event.eventDate}.csv`;
    a.click();
  };

  const resetBudgetForm = () => {
    setBudgetForm({
      eventId: event?.eventId || "",
      eventName: event?.eventName || "",
      eventDate: event?.eventDate || "",
      totalBudget: "",
      decorationBudget: "",
      staffBudget: "",
      soundBudget: "",
      marketingBudget: "",
      cateringBudget: "",
      securityBudget: "",
      transportBudget: "",
      miscellaneousBudget: "",
      notes: "",
      status: "Draft"
    });
  };

  const resetExpenseForm = () => {
    setExpenseForm({
      category: "",
      amount: "",
      description: ""
    });
  };

  const openEditBudget = () => {
    if (!budget) return;
    setBudgetForm({
      eventId: budget.eventId,
      eventName: budget.eventName,
      eventDate: budget.eventDate,
      totalBudget: budget.totalBudget,
      decorationBudget: budget.decorationBudget,
      staffBudget: budget.staffBudget,
      soundBudget: budget.soundBudget,
      marketingBudget: budget.marketingBudget,
      cateringBudget: budget.cateringBudget,
      securityBudget: budget.securityBudget,
      transportBudget: budget.transportBudget,
      miscellaneousBudget: budget.miscellaneousBudget,
      notes: budget.notes || "",
      status: budget.status || "Draft"
    });
    setShowEditModal(true);
  };

  const calculateProgress = (allocated, spent) => {
    if (!allocated) return 0;
    return Math.min(100, (spent / allocated) * 100);
  };

  const getProgressBarClass = (percentage) => {
    if (percentage >= 90) return 'danger';
    if (percentage >= 70) return 'warning';
    return 'success';
  };

  if (!event) return null;

  return (
    <div className="budget-planner">
      <div className="budget-header">
        <div className="budget-title">
          <h3>
            <FontAwesomeIcon icon={faWallet} className="title-icon" />
            Budget Planning: {event.eventName}
          </h3>
          <span className="event-date">{event.eventDate}</span>
        </div>
        <button className="close-btn" onClick={onClose}>
          <FontAwesomeIcon icon={faTimesCircle} />
        </button>
      </div>

      {loading && (
        <div className="budget-loading">
          <div className="spinner"></div>
          <p>Loading budget data...</p>
        </div>
      )}

      {!loading && !budget && (
        <div className="no-budget">
          <div className="no-budget-icon">💰</div>
          <h4>No Budget Plan Found</h4>
          <p>Create a budget plan for this event to track expenses</p>
          <button className="btn-primary" onClick={() => setShowAddModal(true)}>
            <FontAwesomeIcon icon={faPlus} />
            Create Budget
          </button>
        </div>
      )}

      {!loading && budget && (
        <>
          {/* Budget Overview Cards */}
          <div className="budget-overview">
            <div className="overview-card total">
              <div className="card-icon">
                <FontAwesomeIcon icon={faCoins} />
              </div>
              <div className="card-content">
                <span>Total Budget</span>
                <strong>₹{budget.totalBudget?.toLocaleString()}</strong>
              </div>
            </div>
            <div className="overview-card expenses">
              <div className="card-icon">
                <FontAwesomeIcon icon={faMoneyBillWave} />
              </div>
              <div className="card-content">
                <span>Total Expenses</span>
                <strong>₹{budget.totalExpenses?.toLocaleString()}</strong>
              </div>
            </div>
            <div className="overview-card remaining">
              <div className="card-icon">
                <FontAwesomeIcon icon={faWallet} />
              </div>
              <div className="card-content">
                <span>Remaining</span>
                <strong className={budget.remainingBudget >= 0 ? 'text-success' : 'text-danger'}>
                  ₹{(budget.totalBudget - budget.totalExpenses).toLocaleString()}
                </strong>
              </div>
            </div>
            <div className="overview-card utilization">
              <div className="card-icon">
                <FontAwesomeIcon icon={faPercent} />
              </div>
              <div className="card-content">
                <span>Utilization</span>
                <strong>
                  {budget.totalBudget > 0 
                    ? ((budget.totalExpenses / budget.totalBudget) * 100).toFixed(1) 
                    : 0}%
                </strong>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="budget-progress">
            <div className="progress-label">
              <span>Budget Utilization</span>
              <span>{budget.totalBudget > 0 
                ? ((budget.totalExpenses / budget.totalBudget) * 100).toFixed(1) 
                : 0}%</span>
            </div>
            <div className="progress-bar-container">
              <div 
                className={`progress-fill ${getProgressBarClass((budget.totalExpenses / budget.totalBudget) * 100)}`}
                style={{ width: `${Math.min(100, (budget.totalExpenses / budget.totalBudget) * 100)}%` }}
              ></div>
            </div>
          </div>

          {/* Budget Breakdown Table */}
          <div className="budget-breakdown">
            <h4>Budget Breakdown</h4>
            <table className="budget-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Allocated (₹)</th>
                  <th>Spent (₹)</th>
                  <th>Remaining (₹)</th>
                  <th>Progress</th>
                </tr>
              </thead>
              <tbody>
                {budget.decorationBudget > 0 && (
                  <tr>
                    <td>
                      <FontAwesomeIcon icon={faBrush} className="category-icon" />
                      Decoration
                    </td>
                    <td>{budget.decorationBudget.toLocaleString()}</td>
                    <td className="expense-amount">
                      {expenses.filter(e => e.category === 'Decoration').reduce((sum, e) => sum + e.amount, 0).toLocaleString()}
                    </td>
                    <td className={budget.decorationBudget - expenses.filter(e => e.category === 'Decoration').reduce((sum, e) => sum + e.amount, 0) >= 0 ? 'remaining-positive' : 'remaining-negative'}>
                      {(budget.decorationBudget - expenses.filter(e => e.category === 'Decoration').reduce((sum, e) => sum + e.amount, 0)).toLocaleString()}
                    </td>
                    <td>
                      <div className="progress-cell">
                        <div className="cell-progress-bar">
                          <div 
                            className={`progress-fill ${getProgressBarClass(calculateProgress(budget.decorationBudget, expenses.filter(e => e.category === 'Decoration').reduce((sum, e) => sum + e.amount, 0)))}`}
                            style={{ width: `${calculateProgress(budget.decorationBudget, expenses.filter(e => e.category === 'Decoration').reduce((sum, e) => sum + e.amount, 0))}%` }}
                          ></div>
                        </div>
                        <span>{calculateProgress(budget.decorationBudget, expenses.filter(e => e.category === 'Decoration').reduce((sum, e) => sum + e.amount, 0)).toFixed(1)}%</span>
                      </div>
                    </td>
                  </tr>
                )}

                {budget.staffBudget > 0 && (
                  <tr>
                    <td>
                      <FontAwesomeIcon icon={faUsers} className="category-icon" />
                      Staff
                    </td>
                    <td>{budget.staffBudget.toLocaleString()}</td>
                    <td className="expense-amount">
                      {expenses.filter(e => e.category === 'Staff').reduce((sum, e) => sum + e.amount, 0).toLocaleString()}
                    </td>
                    <td className={budget.staffBudget - expenses.filter(e => e.category === 'Staff').reduce((sum, e) => sum + e.amount, 0) >= 0 ? 'remaining-positive' : 'remaining-negative'}>
                      {(budget.staffBudget - expenses.filter(e => e.category === 'Staff').reduce((sum, e) => sum + e.amount, 0)).toLocaleString()}
                    </td>
                    <td>
                      <div className="progress-cell">
                        <div className="cell-progress-bar">
                          <div 
                            className={`progress-fill ${getProgressBarClass(calculateProgress(budget.staffBudget, expenses.filter(e => e.category === 'Staff').reduce((sum, e) => sum + e.amount, 0)))}`}
                            style={{ width: `${calculateProgress(budget.staffBudget, expenses.filter(e => e.category === 'Staff').reduce((sum, e) => sum + e.amount, 0))}%` }}
                          ></div>
                        </div>
                        <span>{calculateProgress(budget.staffBudget, expenses.filter(e => e.category === 'Staff').reduce((sum, e) => sum + e.amount, 0)).toFixed(1)}%</span>
                      </div>
                    </td>
                  </tr>
                )}

                {budget.soundBudget > 0 && (
                  <tr>
                    <td>
                      <FontAwesomeIcon icon={faMusic} className="category-icon" />
                      Sound
                    </td>
                    <td>{budget.soundBudget.toLocaleString()}</td>
                    <td className="expense-amount">
                      {expenses.filter(e => e.category === 'Sound').reduce((sum, e) => sum + e.amount, 0).toLocaleString()}
                    </td>
                    <td className={budget.soundBudget - expenses.filter(e => e.category === 'Sound').reduce((sum, e) => sum + e.amount, 0) >= 0 ? 'remaining-positive' : 'remaining-negative'}>
                      {(budget.soundBudget - expenses.filter(e => e.category === 'Sound').reduce((sum, e) => sum + e.amount, 0)).toLocaleString()}
                    </td>
                    <td>
                      <div className="progress-cell">
                        <div className="cell-progress-bar">
                          <div 
                            className={`progress-fill ${getProgressBarClass(calculateProgress(budget.soundBudget, expenses.filter(e => e.category === 'Sound').reduce((sum, e) => sum + e.amount, 0)))}`}
                            style={{ width: `${calculateProgress(budget.soundBudget, expenses.filter(e => e.category === 'Sound').reduce((sum, e) => sum + e.amount, 0))}%` }}
                          ></div>
                        </div>
                        <span>{calculateProgress(budget.soundBudget, expenses.filter(e => e.category === 'Sound').reduce((sum, e) => sum + e.amount, 0)).toFixed(1)}%</span>
                      </div>
                    </td>
                  </tr>
                )}

                {budget.marketingBudget > 0 && (
                  <tr>
                    <td>
                      <FontAwesomeIcon icon={faChartBar} className="category-icon" />
                      Marketing
                    </td>
                    <td>{budget.marketingBudget.toLocaleString()}</td>
                    <td className="expense-amount">
                      {expenses.filter(e => e.category === 'Marketing').reduce((sum, e) => sum + e.amount, 0).toLocaleString()}
                    </td>
                    <td className={budget.marketingBudget - expenses.filter(e => e.category === 'Marketing').reduce((sum, e) => sum + e.amount, 0) >= 0 ? 'remaining-positive' : 'remaining-negative'}>
                      {(budget.marketingBudget - expenses.filter(e => e.category === 'Marketing').reduce((sum, e) => sum + e.amount, 0)).toLocaleString()}
                    </td>
                    <td>
                      <div className="progress-cell">
                        <div className="cell-progress-bar">
                          <div 
                            className={`progress-fill ${getProgressBarClass(calculateProgress(budget.marketingBudget, expenses.filter(e => e.category === 'Marketing').reduce((sum, e) => sum + e.amount, 0)))}`}
                            style={{ width: `${calculateProgress(budget.marketingBudget, expenses.filter(e => e.category === 'Marketing').reduce((sum, e) => sum + e.amount, 0))}%` }}
                          ></div>
                        </div>
                        <span>{calculateProgress(budget.marketingBudget, expenses.filter(e => e.category === 'Marketing').reduce((sum, e) => sum + e.amount, 0)).toFixed(1)}%</span>
                      </div>
                    </td>
                  </tr>
                )}

                {budget.cateringBudget > 0 && (
                  <tr>
                    <td>
                      <FontAwesomeIcon icon={faUtensils} className="category-icon" />
                      Catering
                    </td>
                    <td>{budget.cateringBudget.toLocaleString()}</td>
                    <td className="expense-amount">
                      {expenses.filter(e => e.category === 'Catering').reduce((sum, e) => sum + e.amount, 0).toLocaleString()}
                    </td>
                    <td className={budget.cateringBudget - expenses.filter(e => e.category === 'Catering').reduce((sum, e) => sum + e.amount, 0) >= 0 ? 'remaining-positive' : 'remaining-negative'}>
                      {(budget.cateringBudget - expenses.filter(e => e.category === 'Catering').reduce((sum, e) => sum + e.amount, 0)).toLocaleString()}
                    </td>
                    <td>
                      <div className="progress-cell">
                        <div className="cell-progress-bar">
                          <div 
                            className={`progress-fill ${getProgressBarClass(calculateProgress(budget.cateringBudget, expenses.filter(e => e.category === 'Catering').reduce((sum, e) => sum + e.amount, 0)))}`}
                            style={{ width: `${calculateProgress(budget.cateringBudget, expenses.filter(e => e.category === 'Catering').reduce((sum, e) => sum + e.amount, 0))}%` }}
                          ></div>
                        </div>
                        <span>{calculateProgress(budget.cateringBudget, expenses.filter(e => e.category === 'Catering').reduce((sum, e) => sum + e.amount, 0)).toFixed(1)}%</span>
                      </div>
                    </td>
                  </tr>
                )}

                {budget.securityBudget > 0 && (
                  <tr>
                    <td>
                      <FontAwesomeIcon icon={faShieldAlt} className="category-icon" />
                      Security
                    </td>
                    <td>{budget.securityBudget.toLocaleString()}</td>
                    <td className="expense-amount">
                      {expenses.filter(e => e.category === 'Security').reduce((sum, e) => sum + e.amount, 0).toLocaleString()}
                    </td>
                    <td className={budget.securityBudget - expenses.filter(e => e.category === 'Security').reduce((sum, e) => sum + e.amount, 0) >= 0 ? 'remaining-positive' : 'remaining-negative'}>
                      {(budget.securityBudget - expenses.filter(e => e.category === 'Security').reduce((sum, e) => sum + e.amount, 0)).toLocaleString()}
                    </td>
                    <td>
                      <div className="progress-cell">
                        <div className="cell-progress-bar">
                          <div 
                            className={`progress-fill ${getProgressBarClass(calculateProgress(budget.securityBudget, expenses.filter(e => e.category === 'Security').reduce((sum, e) => sum + e.amount, 0)))}`}
                            style={{ width: `${calculateProgress(budget.securityBudget, expenses.filter(e => e.category === 'Security').reduce((sum, e) => sum + e.amount, 0))}%` }}
                          ></div>
                        </div>
                        <span>{calculateProgress(budget.securityBudget, expenses.filter(e => e.category === 'Security').reduce((sum, e) => sum + e.amount, 0)).toFixed(1)}%</span>
                      </div>
                    </td>
                  </tr>
                )}

                {budget.transportBudget > 0 && (
                  <tr>
                    <td>
                      <FontAwesomeIcon icon={faTruck} className="category-icon" />
                      Transport
                    </td>
                    <td>{budget.transportBudget.toLocaleString()}</td>
                    <td className="expense-amount">
                      {expenses.filter(e => e.category === 'Transport').reduce((sum, e) => sum + e.amount, 0).toLocaleString()}
                    </td>
                    <td className={budget.transportBudget - expenses.filter(e => e.category === 'Transport').reduce((sum, e) => sum + e.amount, 0) >= 0 ? 'remaining-positive' : 'remaining-negative'}>
                      {(budget.transportBudget - expenses.filter(e => e.category === 'Transport').reduce((sum, e) => sum + e.amount, 0)).toLocaleString()}
                    </td>
                    <td>
                      <div className="progress-cell">
                        <div className="cell-progress-bar">
                          <div 
                            className={`progress-fill ${getProgressBarClass(calculateProgress(budget.transportBudget, expenses.filter(e => e.category === 'Transport').reduce((sum, e) => sum + e.amount, 0)))}`}
                            style={{ width: `${calculateProgress(budget.transportBudget, expenses.filter(e => e.category === 'Transport').reduce((sum, e) => sum + e.amount, 0))}%` }}
                          ></div>
                        </div>
                        <span>{calculateProgress(budget.transportBudget, expenses.filter(e => e.category === 'Transport').reduce((sum, e) => sum + e.amount, 0)).toFixed(1)}%</span>
                      </div>
                    </td>
                  </tr>
                )}

                {budget.miscellaneousBudget > 0 && (
                  <tr>
                    <td>
                      <FontAwesomeIcon icon={faEllipsisH} className="category-icon" />
                      Miscellaneous
                    </td>
                    <td>{budget.miscellaneousBudget.toLocaleString()}</td>
                    <td className="expense-amount">
                      {expenses.filter(e => e.category === 'Miscellaneous').reduce((sum, e) => sum + e.amount, 0).toLocaleString()}
                    </td>
                    <td className={budget.miscellaneousBudget - expenses.filter(e => e.category === 'Miscellaneous').reduce((sum, e) => sum + e.amount, 0) >= 0 ? 'remaining-positive' : 'remaining-negative'}>
                      {(budget.miscellaneousBudget - expenses.filter(e => e.category === 'Miscellaneous').reduce((sum, e) => sum + e.amount, 0)).toLocaleString()}
                    </td>
                    <td>
                      <div className="progress-cell">
                        <div className="cell-progress-bar">
                          <div 
                            className={`progress-fill ${getProgressBarClass(calculateProgress(budget.miscellaneousBudget, expenses.filter(e => e.category === 'Miscellaneous').reduce((sum, e) => sum + e.amount, 0)))}`}
                            style={{ width: `${calculateProgress(budget.miscellaneousBudget, expenses.filter(e => e.category === 'Miscellaneous').reduce((sum, e) => sum + e.amount, 0))}%` }}
                          ></div>
                        </div>
                        <span>{calculateProgress(budget.miscellaneousBudget, expenses.filter(e => e.category === 'Miscellaneous').reduce((sum, e) => sum + e.amount, 0)).toFixed(1)}%</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td><strong>TOTAL</strong></td>
                  <td><strong>₹{budget.totalBudget?.toLocaleString()}</strong></td>
                  <td><strong className="expense-amount">₹{budget.totalExpenses?.toLocaleString()}</strong></td>
                  <td><strong className={(budget.totalBudget - budget.totalExpenses) >= 0 ? 'remaining-positive' : 'remaining-negative'}>
                    ₹{(budget.totalBudget - budget.totalExpenses).toLocaleString()}
                  </strong></td>
                  <td>
                    <div className="progress-cell">
                      <div className="cell-progress-bar">
                        <div 
                          className={`progress-fill ${getProgressBarClass((budget.totalExpenses / budget.totalBudget) * 100)}`}
                          style={{ width: `${Math.min(100, (budget.totalExpenses / budget.totalBudget) * 100)}%` }}
                        ></div>
                      </div>
                      <span>{((budget.totalExpenses / budget.totalBudget) * 100).toFixed(1)}%</span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Action Buttons */}
          <div className="budget-actions">
            <button className="btn-primary" onClick={() => setShowExpenseModal(true)}>
              <FontAwesomeIcon icon={faPlus} />
              Add Expense
            </button>
            <button className="btn-secondary" onClick={openEditBudget}>
              <FontAwesomeIcon icon={faPencilAlt} />
              Edit Budget
            </button>
            <button className="btn-danger" onClick={deleteBudget}>
              <FontAwesomeIcon icon={faTrashAlt} />
              Delete Budget
            </button>
            <button className="btn-export" onClick={exportBudgetReport}>
              <FontAwesomeIcon icon={faDownload} />
              Export Report
            </button>
          </div>

          {/* Recent Expenses */}
          <div className="recent-expenses">
            <div className="expenses-header">
              <h4>
                <FontAwesomeIcon icon={faHistory} className="header-icon" />
                Recent Expenses
              </h4>
            </div>
            
            {expenses.length === 0 ? (
              <div className="no-expenses">
                <p>No expenses recorded yet. Click "Add Expense" to track spending.</p>
              </div>
            ) : (
              <table className="expenses-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Category</th>
                    <th>Description</th>
                    <th>Amount</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map((expense) => (
                    <tr key={expense.expenseId}>
                      <td>{expense.expenseDate}</td>
                      <td>
                        <span className="category-badge">{expense.category}</span>
                      </td>
                      <td>{expense.description}</td>
                      <td className="amount">₹{expense.amount.toLocaleString()}</td>
                      <td>
                        <button className="btn-icon small delete" onClick={() => deleteExpense(expense.expenseId)}>
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Notes */}
          {budget.notes && (
            <div className="budget-notes">
              <h4>Notes</h4>
              <p>{budget.notes}</p>
            </div>
          )}
        </>
      )}

      {/* Add Budget Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>
                <FontAwesomeIcon icon={faPlus} className="modal-icon" />
                Create Budget for {event.eventName}
              </h3>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>
                <FontAwesomeIcon icon={faTimesCircle} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Total Budget (₹)</label>
                <input
                  type="number"
                  value={budgetForm.totalBudget}
                  onChange={(e) => setBudgetForm({...budgetForm, totalBudget: e.target.value})}
                  placeholder="Enter total budget"
                />
              </div>

              <h4 className="section-subtitle">Category Allocation</h4>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Decoration Budget</label>
                  <input
                    type="number"
                    value={budgetForm.decorationBudget}
                    onChange={(e) => setBudgetForm({...budgetForm, decorationBudget: e.target.value})}
                    placeholder="Enter amount"
                  />
                </div>
                <div className="form-group">
                  <label>Staff Budget</label>
                  <input
                    type="number"
                    value={budgetForm.staffBudget}
                    onChange={(e) => setBudgetForm({...budgetForm, staffBudget: e.target.value})}
                    placeholder="Enter amount"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Sound Budget</label>
                  <input
                    type="number"
                    value={budgetForm.soundBudget}
                    onChange={(e) => setBudgetForm({...budgetForm, soundBudget: e.target.value})}
                    placeholder="Enter amount"
                  />
                </div>
                <div className="form-group">
                  <label>Marketing Budget</label>
                  <input
                    type="number"
                    value={budgetForm.marketingBudget}
                    onChange={(e) => setBudgetForm({...budgetForm, marketingBudget: e.target.value})}
                    placeholder="Enter amount"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Catering Budget</label>
                  <input
                    type="number"
                    value={budgetForm.cateringBudget}
                    onChange={(e) => setBudgetForm({...budgetForm, cateringBudget: e.target.value})}
                    placeholder="Enter amount"
                  />
                </div>
                <div className="form-group">
                  <label>Security Budget</label>
                  <input
                    type="number"
                    value={budgetForm.securityBudget}
                    onChange={(e) => setBudgetForm({...budgetForm, securityBudget: e.target.value})}
                    placeholder="Enter amount"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Transport Budget</label>
                  <input
                    type="number"
                    value={budgetForm.transportBudget}
                    onChange={(e) => setBudgetForm({...budgetForm, transportBudget: e.target.value})}
                    placeholder="Enter amount"
                  />
                </div>
                <div className="form-group">
                  <label>Miscellaneous Budget</label>
                  <input
                    type="number"
                    value={budgetForm.miscellaneousBudget}
                    onChange={(e) => setBudgetForm({...budgetForm, miscellaneousBudget: e.target.value})}
                    placeholder="Enter amount"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  value={budgetForm.status}
                  onChange={(e) => setBudgetForm({...budgetForm, status: e.target.value})}
                >
                  <option value="Draft">Draft</option>
                  <option value="Approved">Approved</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={budgetForm.notes}
                  onChange={(e) => setBudgetForm({...budgetForm, notes: e.target.value})}
                  placeholder="Add any notes..."
                  rows="3"
                ></textarea>
              </div>

              {budgetForm.totalBudget && (
                <div className="budget-summary">
                  <h4>Summary</h4>
                  <div className="summary-row">
                    <span>Total Allocated:</span>
                    <strong>
                      ₹{(
                        (parseFloat(budgetForm.decorationBudget) || 0) +
                        (parseFloat(budgetForm.staffBudget) || 0) +
                        (parseFloat(budgetForm.soundBudget) || 0) +
                        (parseFloat(budgetForm.marketingBudget) || 0) +
                        (parseFloat(budgetForm.cateringBudget) || 0) +
                        (parseFloat(budgetForm.securityBudget) || 0) +
                        (parseFloat(budgetForm.transportBudget) || 0) +
                        (parseFloat(budgetForm.miscellaneousBudget) || 0)
                      ).toLocaleString()}
                    </strong>
                  </div>
                  <div className="summary-row">
                    <span>Remaining:</span>
                    <strong className={(
                      (parseFloat(budgetForm.totalBudget) || 0) -
                      (parseFloat(budgetForm.decorationBudget) || 0) -
                      (parseFloat(budgetForm.staffBudget) || 0) -
                      (parseFloat(budgetForm.soundBudget) || 0) -
                      (parseFloat(budgetForm.marketingBudget) || 0) -
                      (parseFloat(budgetForm.cateringBudget) || 0) -
                      (parseFloat(budgetForm.securityBudget) || 0) -
                      (parseFloat(budgetForm.transportBudget) || 0) -
                      (parseFloat(budgetForm.miscellaneousBudget) || 0)
                    ) >= 0 ? 'text-success' : 'text-danger'}>
                      ₹{(
                        (parseFloat(budgetForm.totalBudget) || 0) -
                        (parseFloat(budgetForm.decorationBudget) || 0) -
                        (parseFloat(budgetForm.staffBudget) || 0) -
                        (parseFloat(budgetForm.soundBudget) || 0) -
                        (parseFloat(budgetForm.marketingBudget) || 0) -
                        (parseFloat(budgetForm.cateringBudget) || 0) -
                        (parseFloat(budgetForm.securityBudget) || 0) -
                        (parseFloat(budgetForm.transportBudget) || 0) -
                        (parseFloat(budgetForm.miscellaneousBudget) || 0)
                      ).toLocaleString()}
                    </strong>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowAddModal(false)}>
                <FontAwesomeIcon icon={faBan} />
                Cancel
              </button>
              <button className="btn-primary" onClick={createBudget} disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Creating...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} />
                    Create Budget
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Budget Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>
                <FontAwesomeIcon icon={faPencilAlt} className="modal-icon" />
                Edit Budget
              </h3>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>
                <FontAwesomeIcon icon={faTimesCircle} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Total Budget (₹)</label>
                <input
                  type="number"
                  value={budgetForm.totalBudget}
                  onChange={(e) => setBudgetForm({...budgetForm, totalBudget: e.target.value})}
                />
              </div>

              <h4 className="section-subtitle">Category Allocation</h4>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Decoration Budget</label>
                  <input
                    type="number"
                    value={budgetForm.decorationBudget}
                    onChange={(e) => setBudgetForm({...budgetForm, decorationBudget: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Staff Budget</label>
                  <input
                    type="number"
                    value={budgetForm.staffBudget}
                    onChange={(e) => setBudgetForm({...budgetForm, staffBudget: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Sound Budget</label>
                  <input
                    type="number"
                    value={budgetForm.soundBudget}
                    onChange={(e) => setBudgetForm({...budgetForm, soundBudget: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Marketing Budget</label>
                  <input
                    type="number"
                    value={budgetForm.marketingBudget}
                    onChange={(e) => setBudgetForm({...budgetForm, marketingBudget: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Catering Budget</label>
                  <input
                    type="number"
                    value={budgetForm.cateringBudget}
                    onChange={(e) => setBudgetForm({...budgetForm, cateringBudget: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Security Budget</label>
                  <input
                    type="number"
                    value={budgetForm.securityBudget}
                    onChange={(e) => setBudgetForm({...budgetForm, securityBudget: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Transport Budget</label>
                  <input
                    type="number"
                    value={budgetForm.transportBudget}
                    onChange={(e) => setBudgetForm({...budgetForm, transportBudget: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Miscellaneous Budget</label>
                  <input
                    type="number"
                    value={budgetForm.miscellaneousBudget}
                    onChange={(e) => setBudgetForm({...budgetForm, miscellaneousBudget: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  value={budgetForm.status}
                  onChange={(e) => setBudgetForm({...budgetForm, status: e.target.value})}
                >
                  <option value="Draft">Draft</option>
                  <option value="Approved">Approved</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={budgetForm.notes}
                  onChange={(e) => setBudgetForm({...budgetForm, notes: e.target.value})}
                  rows="3"
                ></textarea>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowEditModal(false)}>
                <FontAwesomeIcon icon={faBan} />
                Cancel
              </button>
              <button className="btn-primary" onClick={updateBudget} disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Updating...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} />
                    Update Budget
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Expense Modal */}
      {showExpenseModal && budget && (
        <div className="modal-overlay">
          <div className="modal-container expense-modal">
            <div className="modal-header">
              <h3>
                <FontAwesomeIcon icon={faPlus} className="modal-icon" />
                Add Expense for {event.eventName}
              </h3>
              <button className="modal-close" onClick={() => setShowExpenseModal(false)}>
                <FontAwesomeIcon icon={faTimesCircle} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Category</label>
                <select
                  value={expenseForm.category}
                  onChange={(e) => setExpenseForm({...expenseForm, category: e.target.value})}
                  required
                >
                  <option value="">Select Category</option>
                  <option value="Decoration">Decoration</option>
                  <option value="Staff">Staff</option>
                  <option value="Sound">Sound</option>
                  <option value="Marketing">Marketing</option>
                  <option value="Catering">Catering</option>
                  <option value="Security">Security</option>
                  <option value="Transport">Transport</option>
                  <option value="Miscellaneous">Miscellaneous</option>
                </select>
              </div>

              <div className="form-group">
                <label>Amount (₹)</label>
                <input
                  type="number"
                  value={expenseForm.amount}
                  onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})}
                  placeholder="Enter amount"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  value={expenseForm.description}
                  onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})}
                  placeholder="Enter description"
                  required
                />
              </div>

              <div className="info-message">
                <small>Expense will be recorded for event date: <strong>{event.eventDate}</strong></small>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowExpenseModal(false)}>
                <FontAwesomeIcon icon={faBan} />
                Cancel
              </button>
              <button className="btn-primary" onClick={addExpense} disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Adding...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} />
                    Add Expense
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BudgetPlanner;